import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { ChatCircle, Plus, PushPin, Clock, Chats } from "@phosphor-icons/react";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "general", label: "General" },
  { id: "exam_help", label: "Exam Help" },
  { id: "study_tips", label: "Study Tips" },
  { id: "feedback", label: "Feedback" },
  { id: "announcements", label: "Announcements" },
];

export default function Forum() {
  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "general" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/forum/threads", { params: { category } });
      setThreads(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    setCreating(true);
    try {
      await api.post("/forum/threads", form);
      toast.success("Thread posted");
      setForm({ title: "", body: "", category: "general" });
      setShowCreate(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post"); }
    finally { setCreating(false); }
  };

  return (
    <div>
      <PageHeader
        overline="Community"
        title="Discussion forum"
        subtitle="Ask questions, share study tips, and get help from peers, examiners, and admins."
        action={
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800"
            data-testid="forum-new-btn"
          >
            <Plus size={14} /> {showCreate ? "Cancel" : "New thread"}
          </button>
        }
      />

      {showCreate && (
        <div className="border border-zinc-200 p-6 mb-6" data-testid="forum-create-form">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Start a discussion</div>
          <input
            placeholder="Thread title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3"
            data-testid="forum-title-input"
          />
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3 bg-white"
            data-testid="forum-category-select"
          >
            {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <textarea
            placeholder="Share the details of your question or discussion…"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={5}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3"
            data-testid="forum-body-input"
          />
          <button
            onClick={create}
            disabled={creating}
            className="bg-zinc-900 text-white px-5 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
            data-testid="forum-submit-btn"
          >
            {creating ? "Posting…" : "Post thread"}
          </button>
        </div>
      )}

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="forum-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${category === c.id ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
            data-testid={`forum-cat-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center" data-testid="forum-empty">
          <Chats size={40} className="mx-auto text-zinc-300 mb-3" />
          <div className="text-sm text-zinc-500">No threads in this category yet. Be the first to post.</div>
        </div>
      ) : (
        <div className="border border-zinc-200 divide-y divide-zinc-200" data-testid="forum-thread-list">
          {threads.map((t) => (
            <Link
              to={`/app/forum/${t.thread_id}`}
              key={t.thread_id}
              className="flex items-start gap-4 p-5 hover:bg-zinc-50"
              data-testid={`forum-thread-${t.thread_id}`}
            >
              <div className="mt-1 text-zinc-400">
                {t.pinned ? <PushPin size={18} weight="fill" /> : <ChatCircle size={18} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-medium truncate">{t.title}</div>
                  <span className="text-[10px] uppercase tracking-wider border border-zinc-300 px-2 py-0.5 shrink-0">
                    {t.category?.replace("_", " ")}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 line-clamp-1">{t.body}</div>
                <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-3">
                  <span>by <span className="text-zinc-900 font-medium">{t.author_name}</span></span>
                  <span className="text-zinc-300">·</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {new Date(t.last_activity_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-display font-bold tabular">{t.reply_count || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">replies</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
